import { Component, Input, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastService } from "@core/components/toasts/toasts.service";
import { TimeOccurrenceService } from "services/corporate/timeOccurrence.service";
import { EmployeeService } from "services/corporate/employee.service";
import { EmployeeModel } from "services/models/employee.model";
import { TimeOccurrenceModel } from "services/models/corporate/timeOccurrence.model";
import { combineLatest } from "rxjs";

@Component({
  selector: "app-manage-registrations",
  templateUrl: "./manage-registrations.component.html",
  styleUrls: ["./manage-registrations.component.scss"],
})
export class ManageRegistrationsModalComponent implements OnInit {

  @Input() timeOccurrence: TimeOccurrenceModel;
  employee: EmployeeModel = new EmployeeModel();
  registrations: TimeRegistration[] = [];
  hasValidRegistrations: boolean = false;
  newHour: string = "";

  private registrationMap = [
    { id: "1", label: "1° Entrada", real: "realEntry1", modified: "modifiedEntry1" },
    { id: "2", label: "1° Saída", real: "realDeparture1", modified: "modifiedDeparture1" },
    { id: "3", label: "2° Entrada", real: "realEntry2", modified: "modifiedEntry2" },
    { id: "4", label: "2° Saída", real: "realDeparture2", modified: "modifiedDeparture2" },
    { id: "5", label: "3° Entrada", real: "realEntry3", modified: "modifiedEntry3" },
    { id: "6", label: "3° Saída", real: "realDeparture3", modified: "modifiedDeparture3" },
    { id: "7", label: "4° Entrada", real: "realEntry4", modified: "modifiedEntry4" },
    { id: "8", label: "4° Saída", real: "realDeparture4", modified: "modifiedDeparture4" },
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private _toaster: ToastService,
    private _service: TimeOccurrenceService,
    private _employeeService: EmployeeService
  ) { }

  ngOnInit(): void {
    this.initialRequest();
  }

  private initialRequest() {
    combineLatest([this.getEmployee()]).subscribe(
      ([employee]) => {
        this.employee = employee.data;
        this.registrations = this.setRegistrations();
        this.updateValidRegistrationsFlag();
      }
    );
  }

  private getEmployee = () => this._employeeService.get(this.timeOccurrence.employeeId);

  private setRegistrations(): TimeRegistration[] {
    return this.registrationMap.map(item => {
      const modified = this.timeOccurrence[item.modified];
      const real = this.timeOccurrence[item.real];
      const hour = modified || real;

      return {
        id: item.id,
        label: item.label,
        hour: hour,
        isOriginal: this.isOriginalTime(hour),
      };
    });
  }

  insertNewRegistration(): void {
    const hourToInsert = this.newHour.trim();
    if (!hourToInsert) return;

    if (this.registrations.every(reg => reg.hour && reg.hour !== "00:00:59")) {
      this._toaster.showWarning('Todas as marcações já foram inseridas.');
      return;
    }

    const adjustedTimes = this.getAdjustedTimes();

    const newRegMinutes = this.timeToMinutes(hourToInsert);
    const adjustedNewReg = (() => {
      const last = Math.max(...adjustedTimes.filter(x => x !== Infinity), -1);
      return (newRegMinutes < (last % 1440)) ? newRegMinutes + 1440 : newRegMinutes;
    })();

    let insertIndex = adjustedTimes.findIndex(time => adjustedNewReg < time);
    if (insertIndex === -1) insertIndex = this.registrations.length;

    const currentRegs = [...this.registrations];

    for (let i = currentRegs.length - 1; i > insertIndex; i--) {
      currentRegs[i].hour = currentRegs[i - 1].hour;
      currentRegs[i].isOriginal = currentRegs[i - 1].isOriginal;
    }

    insertIndex = this.hasValidRegistrations ? insertIndex : 0;
    currentRegs[insertIndex].hour = `${hourToInsert}:00`;
    currentRegs[insertIndex].isOriginal = false;

    this.newHour = "";
    this.registrations = this.buildRegistrationsArray(currentRegs);

    this.updateValidRegistrationsFlag();
  }


  removeRegistration(registration: TimeRegistration): void {
    const index = this.registrations.findIndex(r => r.id === registration.id);
    if (index === -1) return;

    const currentRegs = [...this.registrations];

    for (let i = index; i < currentRegs.length - 1; i++) {
      currentRegs[i].hour = currentRegs[i + 1].hour;
      currentRegs[i].isOriginal = currentRegs[i + 1].isOriginal;
    }

    currentRegs[currentRegs.length - 1].hour = "00:00:59";
    currentRegs[currentRegs.length - 1].isOriginal = true;

    this.registrations = this.buildRegistrationsArray(currentRegs);

    this.updateValidRegistrationsFlag();
  }

  private buildRegistrationsArray(source: TimeRegistration[]): TimeRegistration[] {
    return source.map(item => ({
      id: item.id,
      label: item.label,
      hour: item.hour,
      isOriginal: item.isOriginal,
    }));
  }

  updateRegistrations(): void {
    const hasModifications = this.registrations.some(reg => !reg.isOriginal);

    const keys = [
      "modifiedEntry1", "modifiedDeparture1",
      "modifiedEntry2", "modifiedDeparture2",
      "modifiedEntry3", "modifiedDeparture3",
      "modifiedEntry4", "modifiedDeparture4"
    ];

    const toRequest = Object.assign(
      { id: this.timeOccurrence.id },
      ...keys.map((key, index) => ({
        [key]: hasModifications ? this.registrations[index].hour : null
      }))
    );


    this._service.updateRegistrations(toRequest).subscribe({
      next: () => {
        this._toaster.showSuccess("Registros atualizados com sucesso.");
        this.updateRegistrationsInTimeOccurrence(toRequest);
        this.activeModal.close(this.timeOccurrence);
      },
      error: (error) => {
        console.error(error);
        this._toaster.showError("Erro ao atualizar registros.");
      }
    });
  }

  private updateRegistrationsInTimeOccurrence(toRequest: any): void {
    this.registrationMap.forEach(item => {
      this.timeOccurrence[item.modified] = toRequest[item.modified];
    });
  }

  onDismiss(): void {
    this.activeModal.close(false);
  }

  private timeToMinutes(time: string | null): number {
    if (!time) return Infinity;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  }

  private getAdjustedTimes(): number[] {
    const adjusted: number[] = [];
    let dayOffset = 0;
    let previous = -1;

    for (const reg of this.registrations) {
      const minutes = this.timeToMinutes(reg.hour);

      if (minutes !== Infinity && previous !== -1 && minutes < previous) {
        dayOffset += 1440;
      }

      adjusted.push(minutes + dayOffset);
      previous = minutes;
    }

    return adjusted;
  }

  private updateValidRegistrationsFlag(): void {
    this.hasValidRegistrations = this.registrations.some(
      reg => reg.hour && reg.hour !== "00:00:59"
    );
  }

  private isOriginalTime(time: string | null | undefined): boolean {
    const realTimes = [
      this.timeOccurrence.realEntry1, this.timeOccurrence.realDeparture1,
      this.timeOccurrence.realEntry2, this.timeOccurrence.realDeparture2,
      this.timeOccurrence.realEntry3, this.timeOccurrence.realDeparture3,
      this.timeOccurrence.realEntry4, this.timeOccurrence.realDeparture4
    ];

    return realTimes.includes(time);
  }
}

export interface TimeRegistration {
  id: string;
  label: string;
  hour: string;
  isOriginal: boolean;
}
